import * as Adaptive from "adaptivecards";
import { Input } from "adaptivecards";
import { ChoicePropertyEditor, IVersionedChoice, PropertySheet, PropertySheetCategory, TypedCardElementPeer } from "adaptivecards-designer";
import { removeOtherPropertyConfigurations } from "../Utils/CustomElementUtils";
import { getPropertyEditorInstance, PropertyKeys } from "../Pickers/PickerBase";
import { AdaptiveCardTexts, PeoplePicker_UserSourceOptions } from "../../../shared/utils/constants";
import { CardPeoplePickerChoices, renderACPeoplePicker } from "./CardPeoplePicker";

const displayLabelProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "displayLabel", true);
const selectionModeProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "selectionMode", true);
const sourceProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "source", true);
const displayChoicesProperty = new Adaptive.EnumProperty(Adaptive.Versions.v1_0, "displayChoices", []);
const requireFieldProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "requireField", false);

export class AffectedUsersPeoplePicker extends Adaptive.TextInput {
  static readonly displayLabelProperty = displayLabelProperty;
  static readonly selectionModeProperty = selectionModeProperty;
  static readonly sourceProperty = sourceProperty;
  static readonly displayChoicesProperty = displayChoicesProperty;
  static readonly requireFieldProperty = requireFieldProperty;
  static readonly JsonTypeName = "Affected Users";
  static readonly ElementId = "Ticket.AffectedUsers";
  static readonly defaultLabel = "Affected Users";
  static pickerChoices: any[];
  static onCustomValueChange: (input: Input) => void;
  static disableField: boolean;

  //#region Schema

  @Adaptive.property(displayLabelProperty)
  get displayLabel(): string | undefined {
    return this.getValue(AffectedUsersPeoplePicker.displayLabelProperty);
  }

  set displayLabel(value: string | undefined) {
    this.setValue(AffectedUsersPeoplePicker.displayLabelProperty, value);
    this.label = value;
    this.updateLayout();
  }

  @Adaptive.property(selectionModeProperty)
  get selectionMode(): string | undefined {
    return this.getValue(AffectedUsersPeoplePicker.selectionModeProperty);
  }

  set selectionMode(value: string | undefined,) {
    this.setValue(AffectedUsersPeoplePicker.selectionModeProperty, value);
    this.updateLayout();
  }

  @Adaptive.property(sourceProperty)
  get source(): string | undefined {
    return this.getValue(AffectedUsersPeoplePicker.sourceProperty);
  }

  set source(value: string | undefined,) {
    this.setValue(AffectedUsersPeoplePicker.sourceProperty, value);
    this.updateLayout();
  }

  @Adaptive.property(displayChoicesProperty)
  get displayChoices(): any[] | undefined {
    return this.getValue(AffectedUsersPeoplePicker.displayChoicesProperty);
  }

  set displayChoices(value: any[] | undefined,) {
    this.setValue(AffectedUsersPeoplePicker.displayChoicesProperty, value);
    this.updateLayout();
  }

  @Adaptive.property(requireFieldProperty)
  get requireField(): boolean | undefined {
    return this.getValue(AffectedUsersPeoplePicker.requireFieldProperty);
  }

  set requireField(value: boolean | undefined) {
    this.setValue(AffectedUsersPeoplePicker.requireFieldProperty, value);
    this.setValue(AffectedUsersPeoplePicker.isRequiredProperty, value);
    if (value)
      this.setValue(AffectedUsersPeoplePicker.errorMessageProperty, AdaptiveCardTexts.REQUIRE_FIELD);
    this.updateLayout();
  }

  get valueField(): string | undefined {
    return this.getValue(AffectedUsersPeoplePicker.valueProperty);
  }

  set valueField(value: string | undefined) {
    this.setValue(AffectedUsersPeoplePicker.valueProperty, value);
  }

  //#endregion

  protected internalRender(): HTMLElement | undefined {
    this.id = AffectedUsersPeoplePicker.ElementId;
    if ((this?.displayLabel ?? "").length == 0)
      this.displayLabel = AffectedUsersPeoplePicker.defaultLabel;

    if (AffectedUsersPeoplePicker.pickerChoices)
      this.displayChoices = AffectedUsersPeoplePicker.pickerChoices;
    
    this.selectionMode = "multiple";
    
    let source = `${PeoplePicker_UserSourceOptions.EntraMembers}`;
    if ((this?.source ?? "").length == 0)
      this.source = source;

    return renderACPeoplePicker(this, AffectedUsersPeoplePicker.onCustomValueChange, AffectedUsersPeoplePicker.disableField);
  }

  get value(): string {
    return this.getValue(AffectedUsersPeoplePicker.valueProperty);
  }

  getJsonTypeName(): string {
    return AffectedUsersPeoplePicker.JsonTypeName;
  }

  updateLayout(processChildren: boolean = true) {
    super.updateLayout(processChildren);
  }
}

export class AffectedUsersPeoplePickerPeer extends TypedCardElementPeer<AffectedUsersPeoplePicker> {
    static displayLabelProperty = getPropertyEditorInstance(AffectedUsersPeoplePicker.ElementId, PropertyKeys.displayLabel);
    static requireFieldProperty = getPropertyEditorInstance(AffectedUsersPeoplePicker.ElementId, PropertyKeys.requireField);
    static sourceProperty = new ChoicePropertyEditor(Adaptive.Versions.v1_0, "source", "Source", CardPeoplePickerChoices);

    static updateChoices(choices: IVersionedChoice[]) {
      AffectedUsersPeoplePickerPeer.sourceProperty = new ChoicePropertyEditor(Adaptive.Versions.v1_0, "source", "Source", choices);
    }
      
    populatePropertySheet(propertySheet: PropertySheet, defaultCategory: string = PropertySheetCategory.DefaultCategory) {
        
        super.populatePropertySheet(propertySheet, defaultCategory);
        removeOtherPropertyConfigurations(propertySheet);
        
        propertySheet.add(
            defaultCategory,
            AffectedUsersPeoplePickerPeer.idProperty,
            AffectedUsersPeoplePickerPeer.displayLabelProperty,
            AffectedUsersPeoplePickerPeer.requireFieldProperty,
            AffectedUsersPeoplePickerPeer.sourceProperty
        );
    }
}

